import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, Typography, Box, Grow } from '@mui/material';
import { keyframes } from '@mui/system';

const popIn = keyframes`
  0%   { transform: scale(0.3) rotate(-10deg); opacity: 0; }
  65%  { transform: scale(1.08) rotate(1deg);  opacity: 1; }
  100% { transform: scale(1) rotate(0deg); }
`;

const slideUp = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to   { transform: translateY(0);   opacity: 1; }
`;

const shrink = keyframes`
  from { width: 100%; }
  to   { width: 0%;   }
`;

const Transition = React.forwardRef((props, ref) => <Grow ref={ref} {...props} />);

export default function GlobalErrorDialog({ open, message, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [open, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      transitionDuration={{ enter: 280, exit: 150 }}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px -8px rgba(220,38,38,0.14)',
          m: 2
        }
      }}
    >
      {/* Light rose header — soft, not harsh */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
          pt: 4,
          pb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -18,
            right: -18,
            width: 90,
            height: 90,
            borderRadius: '50%',
            bgcolor: 'rgba(239,68,68,0.06)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: -14,
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(239,68,68,0.06)'
          }}
        />

        {/* White circle with red X icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 24px rgba(239,68,68,0.14)',
            animation: `${popIn} 0.48s cubic-bezier(0.34,1.56,0.64,1) 0.1s both`,
            position: 'relative',
            zIndex: 1
          }}
        >
          <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
            <circle cx="20" cy="20" r="18" fill="#fff1f2" />
            <path
              d="M13 13l14 14M27 13L13 27"
              stroke="#ef4444"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {/* Curved white edge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            right: 0,
            height: 26,
            bgcolor: '#ffffff',
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0'
          }}
        />
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          pt: 1.5,
          pb: 3.5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          bgcolor: '#ffffff'
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#1e293b',
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
            mb: 0.75,
            animation: `${slideUp} 0.3s ease 0.25s both`
          }}
        >
          Request Failed
        </Typography>
        <Typography
          sx={{
            color: '#475569',
            fontSize: '0.875rem',
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
            lineHeight: 1.65,
            animation: `${slideUp} 0.3s ease 0.34s both`
          }}
        >
          {message || 'Something went wrong. Please try again.'}
        </Typography>
      </DialogContent>

      {/* Timer bar */}
      <Box sx={{ height: 3, bgcolor: '#fee2e2' }}>
        <Box
          sx={{ height: '100%', bgcolor: '#ef4444', animation: `${shrink} 3.5s linear forwards` }}
        />
      </Box>
    </Dialog>
  );
}

GlobalErrorDialog.propTypes = {
  open: PropTypes.bool,
  message: PropTypes.node,
  onClose: PropTypes.func
};
